<script>
  import { format } from 'svelte-i18n';

  import { ComplianceActions } from '@client/components/compliance-actions';
  import { SelectField, TextField } from '@client/components/fields/standalone';
  import { Porthole } from '@client/components/porthole';
  import { Spinner } from '@client/components/spinner';

  import { UsersService } from '@client/services/users';

  import { userStore } from '@client/stores/user';

  import { userMetadata } from '@client/utils/users';

  import { USER_REQUEST_PARAMS } from '@client/enums';

  import { debounce } from '@common/utils/debounce';

  export let users = [];
  export let originalUsers = [];
  export let selectedUser = { label: '', value: '' };
  export let recipientEmail = '';
  export let actions = [];

  let filterText = '';
  let searching = false;
  let previousSearchText = '';

  const processUsers = (data) => {
    return data.map((user) => {
      const username = user.email.split('@').shift();
      const meta = userMetadata({ ...user, features: $userStore.features });

      return {
        ...meta,
        username,
        label: meta.name,
        value: user.email
      };
    });
  };

  export const onUserSearch = async (text) => {
    const searchText = text || filterText;
    const usernames = users?.map(({ value }) => value.split('@').shift() || []);

    if (
      !searching &&
      searchText.length &&
      searchText !== previousSearchText &&
      JSON.stringify(usernames).toLowerCase().indexOf(searchText.toLowerCase()) === -1
    ) {
      searching = true;
      previousSearchText = searchText;

      const response = await UsersService.getUsers({
        ...USER_REQUEST_PARAMS,
        filterByDate: false,
        perPage: 100,
        debounceQuery: searchText.toLowerCase(),
        query: searchText.toLowerCase()
      });

      const results = processUsers(response?.data || []);

      results.forEach((user) => {
        if (!users.find(({ value }) => value === user.value)) {
          users.push(user);
        }
        if (!originalUsers.find(({ value }) => value === user.value)) {
          originalUsers.push(user);
        }
      });
    } else {
      if (!searchText.length) {
        users = [...originalUsers];
      }
    }

    users = [...users].sort((a, b) => a.name.localeCompare(b.name));
    originalUsers = [...originalUsers].sort((a, b) => a.name.localeCompare(b.name));

    if (text) {
      selectedUser = users?.find(({ value }) => value.split('@').shift() === text) || { value: text, label: text };
    }

    searching = false;
  };

  let userSearch = async () => await onUserSearch();

  let itemFilter = (label, text, option) => {
    const { name, email } = option;
    return JSON.stringify({ name, email }).toLowerCase().indexOf(text.toLowerCase()) > -1;
  };

  const onSenderChange = (item) => {
    selectedUser = { ...item };
  };

  const onRecipientChange = debounce((id, label, value) => {
    recipientEmail = value;
  }, 750);
</script>

<div class="signature-preview-settings">
  <SelectField
    id="selecteduser"
    label={$format('label.EMPLOYEE')}
    editing
    searchable
    placeholder={$format('label.INCLUDED.USERS_PLACEHOLDER')}
    tooltip={$format('label.TOOLTIP.EMPLOYEE')}
    showActions={false}
    {itemFilter}
    bind:filterText
    on:filter={userSearch}
    bind:options={users}
    bind:selected={selectedUser}
    bind:value={selectedUser}
    onChange={onSenderChange}
  >
    <svelte:fragment slot="item" let:item>
      <div class="item" data-testid="select-item">
        {#key item}
          <Porthole details={item} />
        {/key}
        <div class="item-detail">
          <div class="name">{item.name}</div>
          <div class="email">{item.email}</div>
        </div>
      </div>
    </svelte:fragment>

    <svelte:fragment slot="empty">
      {#if searching}
        <div class="empty">
          <Spinner show />
        </div>
      {:else}
        <div class="empty">
          {$format('label.NO_OPTIONS_AVAILABLE')}
        </div>
      {/if}
    </svelte:fragment>
  </SelectField>

  <TextField
    id="recipient-email"
    label={$format('label.RECIPIENT')}
    placeholder={$format('label.PLACEHOLDER.RECIPIENT_EMAIL')}
    tooltip={$format('label.TOOLTIP.RECIPIENT_EMAIL')}
    bind:value={recipientEmail}
    editing={true}
    showActions={false}
    onChange={onRecipientChange}
  />

  {#if actions.length}
    <ComplianceActions bind:actions />
  {/if}
</div>

<style lang="scss">
  .signature-preview-settings {
    :global {
      .svelte-select {
        --height: auto !important;
        --item-height: auto !important;
        --item-line-height: auto !important;
      }
    }

    .empty {
      color: var(--color-gray-600);
      padding: var(--padding-xlarge);
      text-align: center;
    }

    .item {
      align-items: center;
      display: grid;
      grid-template-columns: 48px auto;
      gap: 16px;

      .item-detail {
        .name {
          font-weight: var(--font-weight-medium);
          overflow-wrap: break-word;
          white-space: normal;
          word-break: normal;
        }

        .email {
          color: var(--color-gray-600);
          font-size: var(--font-size-medium);
          text-wrap: wrap;
        }
      }

      &.active {
        .item-detail {
          .email {
            color: var(--color-blue-100);
          }
        }
      }
    }
  }
</style>
